// components/marketplace/ProductCard.tsx
"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import RatingStar from "@/assets/icons/rating-star.svg";

interface ProductCardProps {
  product: {
    id: number;
    name: string;
    slug: string;
    category: string;
    rating: number;
    imageUrl?: string;
    galleryUrls?: string[];
    provider: {
      id: number;
      name: string;
      role: "MANUFACTURER" | "DISTRIBUTOR" | "ADMIN" | string;
    };
  };
}

export default function ProductCard({ product }: ProductCardProps) {
  const allImages = [
    ...(product.imageUrl ? [product.imageUrl] : []),
    ...(product.galleryUrls || []),
  ].filter((url, idx, arr) => arr.indexOf(url) === idx);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!isHovered || allImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
    }, 800);
    return () => clearInterval(interval);
  }, [isHovered, allImages.length]);

  useEffect(() => {
    if (!isHovered) setCurrentImageIndex(0);
  }, [isHovered]);

  const providerRole = product.provider.role?.toUpperCase();
  const roleLabel =
    providerRole === "MANUFACTURER"
      ? "Manufacturer"
      : providerRole === "ADMIN"
      ? "Ren Power"
      : "Distributor";
  const roleColor =
    providerRole === "MANUFACTURER"
      ? "bg-[#17BC2E]"
      : providerRole === "ADMIN"
      ? "bg-[#002301]"
      : "bg-[#FFB600]";

  const categoryDisplay = product.category
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const displayImage =
    allImages[currentImageIndex] || "/assets/img/product-3.jpg";

  return (
    <Link href={`/product/${product.slug}`} className="group block h-full">
      <div
        className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image container */}
        <div className="relative w-full h-48 overflow-hidden bg-gray-50">
          <Image
            src={displayImage}
            alt={product.name}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />

          {/* Provider pill - positioned inside image, top left */}
          <span
            className={`absolute top-3 left-3 z-10 ${roleColor} text-white text-[10px] font-semibold px-2.5 py-1 rounded-full shadow-sm`}
          >
            {roleLabel}
          </span>

          {/* Multi-image dots indicator */}
          {allImages.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {allImages.slice(0, 5).map((_, idx) => (
                <span
                  key={idx}
                  className={`block rounded-full transition-all duration-300 ${
                    idx === currentImageIndex
                      ? "w-3 h-1.5 bg-white"
                      : "w-1.5 h-1.5 bg-white/60"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="px-4 py-3 flex flex-col flex-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
              {categoryDisplay}
            </span>
            <div className="flex items-center gap-0.5">
              <RatingStar className="w-3.5 h-3.5 text-[#FFB600]" />
              {Number(product.rating) > 0 && (
                <span className="text-xs font-medium text-gray-600">
                  {Number(product.rating).toFixed(1)}
                </span>
              )}
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">
            {product.name}
          </h3>
          <button className="mt-auto w-full bg-[#17BC2E] hover:bg-[#002301] text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors duration-200 cursor-pointer">
            Request Quote
          </button>
        </div>
      </div>
    </Link>
  );
}
